// Scroll system — content-driven (see docs/superpowers/specs/
// 2026-06-11-content-driven-scroll-design.md). The DOM is the source of
// truth: ScrollLayoutMeasurer measures the overlay and scrollLayout/
// sceneThresholds derive pages + thresholds. The value below is only the
// pre-measurement fallback for ScrollControls' pages prop.
export const FALLBACK_SCROLL_PAGES = 15.5;

// Static scene values (frozen from your tuned GUI values)
export const STATIC_CAMERA_POS: [number, number, number] = [74, -16, 0.125];
export const STATIC_CAMERA_TARGET: [number, number, number] = [0, 0, 0];

export const DEFAULT_FOG_COLOR = '#cfd6dd';
export const DEFAULT_FOG_NEAR = 0;
export const DEFAULT_FOG_FAR = 900;

// Home hero sky (drei <Sky>). Sun position drives the atmospheric gradient/color;
// shared with the /decks page so both render the same sky.
export const HERO_SKY_DISTANCE = 6000;
export const HERO_SKY_SUN_POSITION: [number, number, number] = [3.8, 0.8, 0];

// /spirit-box uses its own sun: the original low, far-right sun (x 3.8, y 0.8) blew
// out the right side of the frame, exactly where the hero device sits. Lift it
// higher and pull it toward centre so the glow softens instead of forming a hot
// patch on the right. Decoupled from the shared hero sky so home/decks are
// untouched.
export const RADIO_SKY_SUN_POSITION: [number, number, number] = [2.0, 1.8, 0];

// Services: darker dusk-blue fog. The Sky dome (distance 6000) is fully fogged
// by FAR=900, so this color IS the visible upper backdrop behind the services
// copy. Driven by scroll from DEFAULT_FOG_COLOR on home to this across the
// home→services transition so white overlay text stays legible.
export const SERVICES_FOG_COLOR = '#5f7a99';

// Decks section: light-blue sky with cool fog to keep cloud tint readable.
// Fog far is pushed out so the distant clouds (~685 units away) keep their tint
// instead of bleaching into the fog color.
export const DECKS_BG_COLOR = '#9ac0de';
export const DECKS_FOG_COLOR = '#a4c2dd';
export const DECKS_FOG_FAR = 1800;

export const DEFAULT_GROUND_FOG_COLOR = '#d5dde5';
export const DEFAULT_GROUND_FOG_OPACITY = 1.1;
export const DEFAULT_GROUND_FOG_POS_1: [number, number, number] = [-24.0, -9.33, -1.33];
export const DEFAULT_GROUND_FOG_POS_2: [number, number, number] = [26.67, -26.67, 0];

export const DEFAULT_GROUND_FOG_ROT_1: [number, number, number] = [-1.57, 0.46, 0.0];
export const DEFAULT_GROUND_FOG_ROT_2: [number, number, number] = [-1.63, 0.8, 0.38];

// Services should sit "past" the gate (gate is around z≈0, stairs extend to z≈27).
export const SERVICES_CLOUDS_POS: [number, number, number] = [0, -16, -29];
export const DECKS_CLOUDS_POS: [number, number, number] = [0, -674, 156];

export const SERVICES_CAMERA = {
  // Move from HOME (STATIC_CAMERA_POS) forward through the gate toward services.
  // Starts below the cloud mass, looking straight ahead into it.
  pos: [5, -80, -25] as [number, number, number],
  target: [0, -16, -29] as [number, number, number],
};
export const SERVICES_END_CAMERA = {
  // Camera rises vertically through the clouds. Target stays at cloud center
  // so the view pans naturally downward as the camera ascends.
  pos: [5, 50, -25] as [number, number, number],
  target: [0, -16, -29] as [number, number, number],
};
export const DECKS_CAMERA = {
  // Keep camera offset from target (never equal), otherwise lookAt becomes unstable.
  pos: [47, 8, 120] as [number, number, number],
  target: DECKS_CLOUDS_POS,
};

// Section thresholds are no longer constants: they are derived per-measurement
// from [data-scroll-anchor] positions in sceneThresholds.ts (HOME_CHOREO).

// Reviews — pure white background, no fog, no sky dome. The 3D part for
// reviews is empty; this color fills the FBO frame the carousel sits on top of.
export const REVIEWS_BG_COLOR = '#ffffff';
